import { useState, type ReactNode } from 'react';
import {
  GEO_SCOPE_TYPE_LABELS,
  GeographicScopeTypeSchema,
  type GeographicScopeType,
} from '../../lib/api-types';
import type { ReferentialLifecycleAction } from '../../lib/api';
import { toDisplayError } from '../../lib/errors';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { ConfirmDialog } from '../../components/ui/Dialog';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';
import { useGeoEntities } from './hooks';

/** Maps an arbitrary server-provided scope/fallback level onto a French label. */
export function scopeLevelLabel(level: string): string {
  return (GEO_SCOPE_TYPE_LABELS as Record<string, string>)[level] ?? level;
}

// --- Code / name form ---------------------------------------------------------------

/**
 * Minimal controlled {code, name} form shared by every referential create/edit
 * dialog. Extra fields (parent selects…) are rendered via `children`; their
 * state stays in the caller.
 */
export function CodeNameForm({
  idPrefix,
  initial,
  submitLabel = 'Créer',
  pending,
  onCancel,
  onSubmit,
  children,
}: {
  idPrefix: string;
  initial?: { code: string; name: string };
  submitLabel?: string;
  pending?: boolean;
  onCancel: () => void;
  onSubmit: (values: { code: string; name: string }) => void;
  children?: ReactNode;
}): JSX.Element {
  const [code, setCode] = useState(initial?.code ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const submit = () => {
    const next: Record<string, string> = {};
    if (!code.trim()) next.code = 'Le code est requis.';
    if (!name.trim()) next.name = 'Le nom est requis.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({ code: code.trim(), name: name.trim() });
  };

  return (
    <form
      className="space-y-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div>
        <Label htmlFor={`${idPrefix}-code`}>Code</Label>
        <Input
          id={`${idPrefix}-code`}
          aria-invalid={Boolean(errors.code)}
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        {errors.code ? <p className="mt-1 text-sm text-red-600">{errors.code}</p> : null}
      </div>
      <div>
        <Label htmlFor={`${idPrefix}-name`}>Nom</Label>
        <Input
          id={`${idPrefix}-name`}
          aria-invalid={Boolean(errors.name)}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        {errors.name ? <p className="mt-1 text-sm text-red-600">{errors.name}</p> : null}
      </div>
      {children}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          Annuler
        </Button>
        <Button type="submit" loading={pending}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

// --- Geographic scope picker ----------------------------------------------------------

export interface ScopeValue {
  scopeType: GeographicScopeType | '';
  scopeId: string;
}

export const EMPTY_SCOPE: ScopeValue = { scopeType: '', scopeId: '' };

/**
 * Cascading scope selector: type de portée → pays → (ville → quartier | catégorie
 * de zone). Emits `{scopeType, scopeId}` once the terminal entity is chosen; any
 * upstream change resets the downstream selection.
 */
export function ScopePicker({
  idPrefix,
  value,
  onChange,
  typeLabel = 'Type de portée',
}: {
  idPrefix: string;
  value: ScopeValue;
  onChange: (next: ScopeValue) => void;
  typeLabel?: string;
}): JSX.Element {
  const [countryId, setCountryId] = useState('');
  const [cityId, setCityId] = useState('');

  const type = value.scopeType;
  const countriesQuery = useGeoEntities('countries', {}, type !== '');
  const citiesQuery = useGeoEntities(
    'cities',
    { countryId },
    (type === 'CITY' || type === 'DISTRICT') && countryId !== '',
  );
  const districtsQuery = useGeoEntities(
    'districts',
    { cityId },
    type === 'DISTRICT' && cityId !== '',
  );
  const zonesQuery = useGeoEntities(
    'zone-categories',
    { countryId },
    type === 'ZONE_CATEGORY' && countryId !== '',
  );

  const selectType = (next: string) => {
    setCountryId('');
    setCityId('');
    onChange({ scopeType: (next as GeographicScopeType) || '', scopeId: '' });
  };

  const selectCountry = (id: string) => {
    setCityId('');
    if (type === 'COUNTRY') {
      onChange({ scopeType: type, scopeId: id });
      return;
    }
    setCountryId(id);
    onChange({ scopeType: type, scopeId: '' });
  };

  const selectCity = (id: string) => {
    if (type === 'CITY') {
      onChange({ scopeType: type, scopeId: id });
      return;
    }
    setCityId(id);
    onChange({ scopeType: type, scopeId: '' });
  };

  return (
    <div className="space-y-3">
      <div>
        <Label htmlFor={`${idPrefix}-scope-type`}>{typeLabel}</Label>
        <Select
          id={`${idPrefix}-scope-type`}
          value={type}
          onChange={(e) => selectType(e.target.value)}
        >
          <option value="">— Choisir —</option>
          {GeographicScopeTypeSchema.options.map((t) => (
            <option key={t} value={t}>
              {GEO_SCOPE_TYPE_LABELS[t]}
            </option>
          ))}
        </Select>
      </div>

      {type !== '' ? (
        <div>
          <Label htmlFor={`${idPrefix}-scope-country`}>Pays</Label>
          <Select
            id={`${idPrefix}-scope-country`}
            value={type === 'COUNTRY' ? value.scopeId : countryId}
            onChange={(e) => selectCountry(e.target.value)}
          >
            <option value="">— Choisir un pays —</option>
            {(countriesQuery.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.code})
              </option>
            ))}
          </Select>
        </div>
      ) : null}

      {(type === 'CITY' || type === 'DISTRICT') && countryId !== '' ? (
        <div>
          <Label htmlFor={`${idPrefix}-scope-city`}>Ville</Label>
          <Select
            id={`${idPrefix}-scope-city`}
            value={type === 'CITY' ? value.scopeId : cityId}
            onChange={(e) => selectCity(e.target.value)}
          >
            <option value="">— Choisir une ville —</option>
            {(citiesQuery.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.code})
              </option>
            ))}
          </Select>
        </div>
      ) : null}

      {type === 'DISTRICT' && cityId !== '' ? (
        <div>
          <Label htmlFor={`${idPrefix}-scope-district`}>Quartier</Label>
          <Select
            id={`${idPrefix}-scope-district`}
            value={value.scopeId}
            onChange={(e) => onChange({ scopeType: type, scopeId: e.target.value })}
          >
            <option value="">— Choisir un quartier —</option>
            {(districtsQuery.data ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} ({d.code})
              </option>
            ))}
          </Select>
        </div>
      ) : null}

      {type === 'ZONE_CATEGORY' && countryId !== '' ? (
        <div>
          <Label htmlFor={`${idPrefix}-scope-zone`}>Catégorie de zone</Label>
          <Select
            id={`${idPrefix}-scope-zone`}
            value={value.scopeId}
            onChange={(e) => onChange({ scopeType: type, scopeId: e.target.value })}
          >
            <option value="">— Choisir une catégorie —</option>
            {(zonesQuery.data ?? []).map((zc) => (
              <option key={zc.id} value={zc.id}>
                {zc.name} ({zc.code})
              </option>
            ))}
          </Select>
        </div>
      ) : null}
    </div>
  );
}

// --- Lifecycle buttons -------------------------------------------------------------------

const LIFECYCLE_LABELS: Record<ReferentialLifecycleAction, string> = {
  submit: 'Soumettre',
  approve: 'Approuver',
  reject: 'Rejeter',
};

/**
 * Soumettre / Approuver / Rejeter with a ConfirmDialog each. The buttons are
 * always offered: whether a transition is legal (statut, quatre-yeux…) is the
 * SERVER's decision — its refusal (403/409) is rendered verbatim.
 */
export function LifecycleButtons({
  entityLabel,
  pending,
  error,
  onAction,
}: {
  entityLabel: string;
  pending: boolean;
  error: unknown;
  onAction: (action: ReferentialLifecycleAction) => void;
}): JSX.Element {
  const [pendingAction, setPendingAction] = useState<ReferentialLifecycleAction | null>(null);

  return (
    <div className="space-y-3">
      {error ? (
        <Alert variant="error" details={toDisplayError(error).details}>
          {toDisplayError(error).message}
        </Alert>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {(Object.keys(LIFECYCLE_LABELS) as ReferentialLifecycleAction[]).map((action) => (
          <Button
            key={action}
            type="button"
            variant={action === 'reject' ? 'destructive' : 'secondary'}
            size="sm"
            disabled={pending}
            onClick={() => setPendingAction(action)}
          >
            {LIFECYCLE_LABELS[action]}
          </Button>
        ))}
      </div>
      <ConfirmDialog
        open={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        onConfirm={() => {
          if (pendingAction) onAction(pendingAction);
          setPendingAction(null);
        }}
        title="Confirmer l’action"
        description={
          pendingAction ? `${LIFECYCLE_LABELS[pendingAction]} ${entityLabel} ?` : undefined
        }
        confirmLabel="Confirmer"
        destructive={pendingAction === 'reject'}
        loading={pending}
      />
    </div>
  );
}
